import React, { useState, useMemo } from 'react';
import { Search, ChevronDown } from 'lucide-react';
import styles from './InvoiceTable.module.scss';
import { Thead } from '../Thead/Thead';
import { EmptyRow } from '../EmptyRow/EmptyRow';
import { AddressCell } from '../AddressCell/AddressCell';
import { LineItemsSubtable } from '../LineItemsSubtable/LineItemsSubtable';
import { StatusPill } from '../StatusPill/StatusPill';
import { money } from '../../utils';
import type { InvoiceRecord } from '../../types';

interface InvoiceTableProps {
  rows: InvoiceRecord[];
}

export function InvoiceTable({ rows }: InvoiceTableProps) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const filtered = useMemo(() => {
    if (!query.trim()) return rows;
    const q = query.toLowerCase();
    return rows.filter((r) => r.poNumber.toLowerCase().includes(q));
  }, [rows, query]);

  return (
    <div>
      <div className={styles.searchBar}>
        <div className={styles.searchWrap}>
          <Search size={13} className={styles.searchIcon} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by PO number"
            className={styles.searchInput}
          />
        </div>
      </div>
      <table className={styles.table}>
        <Thead
          cols={[
            'Invoice #',
            'PO #',
            'Date',
            'Due',
            'Delivery address',
            'Amount',
            'Status',
            '',
          ]}
        />
        <tbody>
          {filtered.length === 0 && (
            <EmptyRow label="No invoices match that PO number." span={8} />
          )}
          {filtered.map((r) => {
            const isOpen = !!open[r.id];
            return (
              <React.Fragment key={r.id}>
                <tr
                  className={styles.row}
                  onClick={() => setOpen((o) => ({ ...o, [r.id]: !o[r.id] }))}
                >
                  <td className={styles.number}>{r.number}</td>
                  <td className={styles.poNumber}>{r.poNumber}</td>
                  <td className={styles.text}>{r.date}</td>
                  <td className={styles.text}>{r.dueDate}</td>
                  <AddressCell location={r.location} />
                  <td className={styles.amount}>{money(r.amount)}</td>
                  <td className={styles.statusCell}>
                    <StatusPill status={r.status} />
                  </td>
                  <td className={styles.chevronCell}>
                    <ChevronDown
                      size={15}
                      className={
                        isOpen
                          ? `${styles.chevron} ${styles.chevronOpen}`
                          : styles.chevron
                      }
                    />
                  </td>
                </tr>
                {isOpen && (
                  <tr>
                    <td colSpan={8} className={styles.expandedCell}>
                      <LineItemsSubtable skus={r.skus} />
                    </td>
                  </tr>
                )}
              </React.Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
